'use client';
import { createContext, useCallback, useRef, useState } from 'react';
export const KeyboardShortcutRegistryContext = createContext(null);
/**
 * Context for reading the current list of registered shortcuts.
 * The value is a snapshot array that updates whenever registrations
 * are added or removed. Consumers (like the shortcuts modal) can
 * subscribe to this to render dynamic shortcut lists.
 */
export const ShortcutRegistrationsContext = createContext([]);
/**
 * Internal – used by the provider to manage the stack of active registrations.
 *
 * Registrations are stored in a stack (array). The most recently registered
 * handler sits at the end and is matched first during dispatch (iterate in
 * reverse). This gives context-based priority: a component that mounts later
 * and registers the same keybinding automatically takes precedence. When it
 * unmounts, the cleanup function splices the entry out and the previous
 * handler resumes.
 *
 * The `registrations` state is a React state snapshot that triggers
 * re-renders when the set of registered shortcuts changes, enabling
 * the shortcuts modal to dynamically reflect current registrations.
 */
export function useShortcutRegistryState() {
    const registrationsRef = useRef([]);
    const [registrations, setRegistrations] = useState([]);
    const listenersRef = useRef(new Set());
    const publish = useCallback(() => {
        setRegistrations([...registrationsRef.current]);
        listenersRef.current.forEach((listener) => listener());
    }, []);
    const register = useCallback((reg) => {
        registrationsRef.current.push(reg);
        publish();
        return () => {
            const idx = registrationsRef.current.indexOf(reg);
            if (idx !== -1) {
                registrationsRef.current.splice(idx, 1);
            }
            publish();
        };
    }, [publish]);
    const subscribe = useCallback((listener) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);
    const getDisplayKeybinding = useCallback((id) => {
        const stack = registrationsRef.current;
        for (let i = stack.length - 1; i >= 0; i--) {
            const reg = stack[i];
            if (reg.id === id && reg.isEnabled !== false) {
                // A primitive snapshot stays equal when unrelated registrations change.
                // Empty bindings display no keys, distinct from an absent registration.
                return reg.keybinding[0] ?? '';
            }
        }
        return undefined;
    }, []);
    return { registrationsRef, registrations, register, subscribe, getDisplayKeybinding };
}
//# sourceMappingURL=registryState.js.map